import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useCedulasContext } from 'Context/CedulasPageContext';
import { urlToFile, toBase64 } from 'Utils/helpers';
import {
    formatLocation,
    CONTACT_INFO,
    processSenasParticulares,
    processVestimenta,
} from 'Utils/MissingPersonUtils';
import { LOGO_COBUPEJ_CEDULA, LOGO_JAL_CEDULA, X_ICON, FB_ICON } from 'Assets/index.js';
import { QRCode } from 'antd';
import { PNG_IMP as S, PNG_IMP_L, titularFor } from './styles';
import { DISCLAIMER, waitForPngCapture } from './pngCapture';

const FALLBACK = '/no_image.png';

const loadImage = async (src) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = src;
    await img.decode();
    return img;
};

// Data URL para que html2canvas no ensucie el canvas por CORS. Se decodifica antes para
// caer a la imagen de respaldo si la foto no es valida; la foto se estira al recuadro (S.personImage).
const resolvePhoto = async (ruta) => {
    try {
        const file = await urlToFile(ruta);
        if (file) {
            const dataUrl = await toBase64(file);
            await loadImage(dataUrl);
            return { src: dataUrl };
        }
    } catch (e) { /* Si falla la foto, se intenta la imagen de respaldo. */ }
    await loadImage(FALLBACK);
    return { src: FALLBACK };
};

const measureOverflow = (node) => {
    if (!node) return { overflow: {} };
    const caja = (sel) => {
        const el = node.querySelector(sel);
        if (!el) return undefined;
        const line = parseFloat(getComputedStyle(el).lineHeight) || PNG_IMP_L.line;
        return {
            scrollHeight: el.scrollHeight,
            clientHeight: el.clientHeight,
            chars: (el.textContent || '').length,
            lines: Math.round(el.scrollHeight / line),
        };
    };
    const filas = Array.from(node.querySelectorAll('[data-campo]')).map((el) => ({
        campo: el.getAttribute('data-campo'),
        scrollWidth: el.scrollWidth,
        clientWidth: el.clientWidth,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
    }));
    return {
        overflow: {
            senas: caja('[data-caja="senas"]'),
            vestimenta: caja('[data-caja="vestimenta"]'),
            filas,
        },
    };
};

const Row = ({ label, value, campo }) => (
    <div style={S.dataRow} data-campo={campo}>
        <span style={S.dataLabel}>{label}:&nbsp;</span>
        <span style={S.dataValue}>{value}</span>
    </div>
);

const MissingPersonPngImpresion = forwardRef(({ missingPersonData, onReady, onError }, ref) => {
    const { hasInfoField, verifyAge, verifyStature, formatDate } = useCedulasContext();
    const {
        id_cedula_busqueda, ruta_foto, nombre_completo, edad_momento_desaparicion,
        colonia, municipio, estado, fecha_desaparicion, sexo, complexion, tez,
        ojos_color, genero, estatura, cabello, descripcion_vestimenta,
        descripcion_sena_particular, condicion_localizacion,
    } = missingPersonData;

    const titular = titularFor(condicion_localizacion);
    const ubicacion = formatLocation(colonia, municipio, estado);
    const vestimenta = processVestimenta(descripcion_vestimenta);
    const senas = processSenasParticulares(descripcion_sena_particular);
    const vestimentaText = vestimenta.length ? vestimenta.map((i) => i.formattedText).join('. ') : 'SIN DATOS';
    const senasText = senas.length ? senas.map((i) => i.formattedText).join('. ') : 'SIN DATOS';

    const rootRef = useRef(null);
    const readyFired = useRef(false);
    const [photo, setPhoto] = useState(null);

    const setRoot = (node) => {
        rootRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) ref.current = node;
    };

    useEffect(() => {
        let cancel = false;
        setPhoto(null);
        resolvePhoto(ruta_foto)
            .then((p) => { if (!cancel) setPhoto(p); })
            .catch((error) => {
                if (!cancel) {
                    readyFired.current = true;
                    onError?.(error);
                }
            });
        return () => { cancel = true; };
    }, [ruta_foto, onError]);

    useEffect(() => {
        if (!photo || readyFired.current) return;
        let cancel = false;
        (async () => {
            try {
                const node = rootRef.current;
                await waitForPngCapture(node);
                const diagnostico = measureOverflow(node);
                if (!cancel && !readyFired.current) { readyFired.current = true; onReady?.(diagnostico); }
            } catch (err) {
                if (!cancel && !readyFired.current) { readyFired.current = true; onError?.(err); }
            }
        })();
        return () => { cancel = true; };
    }, [photo, onReady, onError]);

    return (
        <div ref={setRoot} style={S.root}>
            <div style={S.photoBox}>
                {photo?.src && (
                    <img src={photo.src} alt={'Persona desaparecida'} style={S.personImage} />
                )}
            </div>

            <div style={S.contactBox} />
            <div style={S.contactTitle}>¿Tienes información sobre su paradero?</div>
            <div style={S.contactSubtitle}>Comunícate con nosotros</div>
            <div style={S.phoneText}>{CONTACT_INFO.PHONE}</div>
            <div style={S.emailText}>{CONTACT_INFO.EMAIL}</div>
            <div style={S.redesRow}>
                <img src={FB_ICON} alt={'Facebook'} style={S.redesIcon} />
                <img src={X_ICON} alt={'X'} style={S.redesIcon} />
                <span style={S.redesText}>{CONTACT_INFO.SOCIAL_MEDIA}</span>
            </div>
            <div style={S.qrBox}>
                <QRCode
                    type="svg"
                    bordered={false}
                    size={180}
                    value={`${window.location.protocol}//${window.location.host}/cedulas-de-busqueda?id_cedula_busqueda=${id_cedula_busqueda}&qr=true`}
                    style={S.qrCode}
                />
            </div>

            <img src={LOGO_COBUPEJ_CEDULA} alt={'Comisión de Búsqueda'} style={S.logoCobupej} />
            <img src={LOGO_JAL_CEDULA} alt={'Gobierno de Jalisco'} style={S.logoJal} />

            <div style={{ ...S.title, color: titular.color }} data-campo="titular">{titular.text}</div>
            <div style={S.name} data-campo="nombre">{hasInfoField(nombre_completo)}</div>

            <div style={S.fechaBox}>
                <div style={S.fechaLine} data-campo="fecha">
                    <span style={S.fechaLabel}>Se le vio por última vez el:&nbsp;</span>
                    <span style={S.fechaValue}>{formatDate(fecha_desaparicion)}</span>
                </div>
                <div style={S.fechaLine} data-campo="ubicacion">
                    <span style={S.lugarLabel}>En:&nbsp;</span>
                    <span style={S.lugarValue}>{ubicacion}</span>
                </div>
            </div>

            <div style={S.edadBox}>
                <div style={S.dataLabel}>Edad al momento de la desaparición:</div>
                <div style={S.dataValue} data-campo="edad">{verifyAge(edad_momento_desaparicion)}</div>
            </div>
            <div style={S.sexoGeneroBox}>
                <Row label="Sexo" value={hasInfoField(sexo)} campo="sexo" />
                <Row label="Género" value={hasInfoField(genero)} campo="genero" />
            </div>

            <div style={S.fisicasBox}>
                <div style={S.boxHeader}>Características físicas:</div>
                <Row label="Estatura" value={verifyStature(estatura)} campo="estatura" />
                <Row label="Complexión" value={hasInfoField(complexion)} campo="complexion" />
                <Row label="Tez" value={hasInfoField(tez)} campo="tez" />
            </div>
            <div style={S.ojosBox}>
                <Row label="Color de ojos" value={hasInfoField(ojos_color)} campo="ojos" />
                <Row label="Cabello" value={hasInfoField(cabello)} campo="cabello" />
            </div>

            <div style={S.senasBox}>
                <div style={S.boxHeader}>Señas particulares:</div>
                <div style={S.boxGap} />
                <div style={{ ...S.boxText, maxHeight: PNG_IMP_L.senasSpace }} data-caja="senas">{senasText}</div>
            </div>
            <div style={S.vestimentaBox}>
                <div style={S.boxHeader}>Vestimenta:</div>
                <div style={S.boxGap} />
                <div style={{ ...S.boxText, maxHeight: PNG_IMP_L.vestimentaSpace }} data-caja="vestimenta">{vestimentaText}</div>
            </div>

            <div style={S.aviso} data-campo="aviso">{DISCLAIMER}</div>
        </div>
    );
});

MissingPersonPngImpresion.displayName = 'MissingPersonPngImpresion';

export default MissingPersonPngImpresion;
